import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SellerAvatar from "@/components/SellerAvatar";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * BeaconCard — the screenshot-friendly share page winners post to X,
 * Instagram, TikTok, Reddit. Vertical layout, big gold avatar, "I just
 * won the Bidorah Beacon" headline, category, total wins. Tap to copy
 * the share URL, tap to download the rendered PNG (browser-native).
 *
 * Loads `/api/beacon/card/:user_id` once; 404 → friendly empty state.
 */
export default function BeaconCard() {
  const { user_id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api
      .get(`/beacon/card/${user_id}`)
      .then((r) => setData(r.data))
      .catch((e) => {
        if (e?.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [user_id]);

  const shareUrl = `${window.location.origin}/beacon-card/${user_id}`;
  const shareText =
    data && data.user
      ? `I just lit up the Bidorah Beacon ✨ @${data.user.username}\n\n${shareUrl}`
      : "";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Share link copied");
    } catch {
      toast.error("Copy failed — long-press to copy manually");
    }
  };

  // Try the native Web Share API first — on mobile this surfaces
  // TikTok / Instagram / Snap / etc. as one-tap targets. On desktop
  // (or any browser without Web Share) we fall back to copying the
  // link and opening the platform's web URL so the user can paste.
  const shareViaNative = async (platform) => {
    // Friendly URLs to open as a fallback when Web Share is unavailable.
    const FALLBACK_URLS = {
      tiktok:    "https://www.tiktok.com/upload",
      instagram: "https://www.instagram.com/",
    };
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Bidorah Beacon",
          text: shareText,
          url: shareUrl,
        });
        return;
      } catch (err) {
        // User cancelled or share failed — fall through to copy fallback.
        if (err && err.name === "AbortError") return;
      }
    }
    // Fallback: copy link + open the platform so the user can paste.
    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch { /* clipboard may be blocked */ }
    toast.success(`Link copied — paste it into ${platform === "tiktok" ? "TikTok" : "Instagram"}`);
    window.open(FALLBACK_URLS[platform] || "/", "_blank", "noopener");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#070a15] flex items-center justify-center text-white">
        Loading…
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="min-h-screen bg-[#070a15] flex flex-col text-white">
        <Navbar />
        <main className="flex-1 max-w-xl mx-auto px-6 py-20 text-center">
          <p className="text-xl">This seller hasn't earned a Beacon yet.</p>
          <Link
            to="/hall-of-beacons"
            className="inline-flex mt-6 rounded-full bg-amber-300 text-amber-950 px-5 py-2 text-sm font-black"
          >
            Visit the Hall of Beacons →
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const { user, latest_trophy, trophy_count, categories_won, boost_active } = data;

  return (
    <div
      className="min-h-screen flex flex-col bg-[#070a15] text-white"
      data-testid="beacon-card-page"
    >
      <Navbar />
      <main className="flex-1 max-w-2xl mx-auto w-full px-6 py-10 sm:py-16">
        {/* THE CARD — square-ish, designed for IG/Twitter aspect ratios */}
        <div
          id="beacon-share-card"
          className="relative mx-auto rounded-3xl overflow-hidden border border-amber-400/40 shadow-[0_0_80px_rgba(252,211,77,0.25)]"
          style={{
            background:
              "radial-gradient(circle at 50% 0%, rgba(252,211,77,0.18), transparent 55%), linear-gradient(180deg,#0f1424 0%,#070a15 100%)",
          }}
          data-testid="beacon-card-image"
        >
          <div className="absolute inset-0 beacon-stars opacity-50 pointer-events-none" />

          <div className="relative px-8 py-12 sm:px-12 sm:py-16 text-center">
            <p className="text-[10px] sm:text-xs uppercase tracking-[0.4em] text-amber-300/80">
              The Bidorah Beacon
            </p>

            <div className="mt-6 flex justify-center">
              <div className="relative">
                <span className="absolute inset-0 rounded-full bg-amber-300/40 blur-3xl" />
                {/* Force-render: when a profile picture exists we show it
                    inside a glowing gold ring. When it doesn't, we render
                    a premium gold-gradient circle with the initial — far
                    more pristine than the default dark-grey placeholder. */}
                {user.picture ? (
                  <img
                    src={user.picture}
                    alt={`@${user.username}`}
                    className="relative w-[150px] h-[150px] rounded-full object-cover ring-4 ring-amber-300 shadow-[0_0_60px_rgba(252,211,77,0.5)]"
                    data-testid="beacon-card-avatar"
                  />
                ) : (
                  <div
                    className="relative w-[150px] h-[150px] rounded-full ring-4 ring-amber-300 shadow-[0_0_60px_rgba(252,211,77,0.5)] flex items-center justify-center font-display font-black text-7xl text-amber-950"
                    style={{
                      background:
                        "radial-gradient(circle at 30% 30%,#fde68a 0%,#f59e0b 55%,#92400e 100%)",
                    }}
                    data-testid="beacon-card-avatar-fallback"
                  >
                    {(user.username || "?").charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
            </div>

            <h1 className="mt-6 font-display font-black text-4xl sm:text-6xl tracking-tighter leading-[0.95]">
              @{user.username}
            </h1>

            {user.is_founder && (
              <p className="mt-2 text-[10px] uppercase tracking-widest text-amber-300/80 inline-block border border-amber-300/40 rounded-full px-3 py-1">
                Founder Seller
              </p>
            )}

            <div className="mt-8 inline-block rounded-2xl bg-amber-300/10 border border-amber-300/40 px-6 py-4">
              <p className="text-[10px] uppercase tracking-widest text-amber-300/80">
                Just earned
              </p>
              <p className="mt-1 font-display font-black text-2xl sm:text-3xl text-amber-200">
                {latest_trophy.category_label}
              </p>
              <p className="text-xs text-white/60 mt-1 font-mono">
                {latest_trophy.week_iso}
              </p>
            </div>

            <div className="mt-8 grid grid-cols-2 gap-6 text-left max-w-sm mx-auto">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-white/40">
                  Lifetime wins
                </p>
                <p className="font-display font-black text-3xl mt-1">
                  {trophy_count}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-white/40">
                  Categories
                </p>
                <p className="font-display font-black text-3xl mt-1">
                  {categories_won.length}
                </p>
              </div>
            </div>

            {boost_active && (
              <p className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-emerald-400/15 border border-emerald-400/40 px-3 py-1 text-xs font-bold text-emerald-200">
                🌀 Beacon Boost active
              </p>
            )}

            <p className="mt-10 text-xs uppercase tracking-[0.35em] text-white/40">
              bidorah · live auctions
            </p>
          </div>
        </div>

        {/* Action row — 2×2 grid on mobile so every pill is fully
            visible and aligned; single row from sm: up. Equal heights,
            consistent rounded shape, identical padding. */}
        <div
          className="mt-8 grid grid-cols-2 sm:flex sm:flex-wrap sm:justify-center gap-3 max-w-md sm:max-w-none mx-auto"
          data-testid="beacon-card-action-row"
        >
          <button
            onClick={copyLink}
            data-testid="beacon-card-copy"
            className="inline-flex items-center justify-center h-11 rounded-full bg-amber-300 hover:bg-amber-200 text-amber-950 px-4 sm:px-5 text-sm font-black whitespace-nowrap"
          >
            Copy link
          </button>
          <button
            onClick={() => shareViaNative("tiktok")}
            data-testid="beacon-card-tiktok"
            aria-label="Share to TikTok"
            className="inline-flex items-center justify-center gap-2 h-11 rounded-full bg-black hover:bg-zinc-800 text-white border border-white/20 px-4 sm:px-5 text-sm font-bold whitespace-nowrap transition"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#69C9D0" d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.1z" />
              <path fill="#EE1D52" d="M18.59 5.69a4.83 4.83 0 0 1-3.77-4.25V1h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V8.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 4.8 19.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.1z" opacity="0.85" />
              <path fill="#FFF" d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.1z" />
            </svg>
            TikTok
          </button>
          <button
            onClick={() => shareViaNative("instagram")}
            data-testid="beacon-card-instagram"
            aria-label="Share to Instagram"
            className="inline-flex items-center justify-center gap-2 h-11 rounded-full text-white px-4 sm:px-5 text-sm font-bold whitespace-nowrap transition hover:opacity-90"
            style={{ background: "linear-gradient(45deg,#f09433 0%,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888 100%)" }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <rect x="3" y="3" width="18" height="18" rx="5" stroke="white" strokeWidth="1.8" />
              <circle cx="12" cy="12" r="3.6" stroke="white" strokeWidth="1.8" />
              <circle cx="17.4" cy="6.6" r="1.1" fill="white" />
            </svg>
            Instagram
          </button>
          <Link
            to={`/seller/${user.user_id}`}
            data-testid="beacon-card-visit"
            className="inline-flex items-center justify-center h-11 rounded-full border border-white/20 hover:border-white/50 px-4 sm:px-5 text-sm font-bold text-white whitespace-nowrap truncate max-w-full"
          >
            <span className="truncate">Visit @{user.username}</span>
          </Link>
        </div>

        <p className="mt-8 text-center text-xs text-white/40 max-w-md mx-auto">
          Screenshot this card and share it anywhere — every Beacon win is
          permanent and proves the win came from Bidorah's homepage.
        </p>
      </main>
      <Footer />
    </div>
  );
}
